import {Helmet} from 'react-helmet'
import {Container} from '@components/ui/container.tsx'
import {Separator} from '@components/ui/separator.tsx'
import {Header} from '@components/commons/header'
import {Footer} from '@components/commons/footer'

const PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME = 'text-xl font-semibold text-neutral-800 m-0 md:text-display-sm'
const PRIVACY_POLICY_SECTIONS_SUBTITLE_CLASSNAME = 'text-lg font-semibold text-neutral-700 m-0 md:text-xl'
const PRIVACY_POLICY_HIGHLIGHTED_CONTENT_CLASSNAME =
    'flex flex-col gap-3 bg-neutral-50 border border-neutral-200 rounded-lg p-4'
export const PrivacyPolicy = () => {
    return (
        <div className="bg-white min-h-dvh">
            <Helmet title="Privacy Policy - Airbnb Navigate" />
            <Header isPrivate={false} />

            <Container>
                <div className="flex flex-col gap-8 py-8 md:pt-16 md:pb-12 [&_p]:text-md [&_p]:text-neutral-600 [&_p]:m-0 [&_p]:leading-[1.6] [&_li]:text-md [&_li]:text-neutral-600 [&_li]:leading-[1.6] [&_li]:mb-1 [&_ul]:m-0 [&_ul]:pl-5 [&_strong]:font-semibold [&_strong]:text-neutral-800">
                    <div className="flex flex-col gap-4">
                        <h1 className="text-display-lg font-bold text-neutral-900 m-0 md:text-display-2xl">
                            Privacy Policy and Cookies
                        </h1>
                        <p>
                            <strong>Published:</strong> {new Date().toLocaleDateString()}
                        </p>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-4">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>I. Privacy Policy</h2>
                        <p>
                            Airbnb Navigate, operated by Translated S.r.l., a company incorporated under Italian law
                            with registered offices on Via Indonesia, No. 23, 00144, Rome, Italy — in compliance with
                            the requirements in Regulation (EU) 2016/679 of the European Parliament and Council of April
                            27, 2016 (General Data Protection Regulation, hereinafter GDPR) and Italian data protection
                            law — respects and protects the privacy of users of the Navigate platform
                            (navigate.translated.com), putting in place every possible and proportionate effort not to
                            harm the rights of its users.
                        </p>
                        <p>
                            The purpose of this privacy policy is to help users understand the procedures that Navigate
                            uses in the area of privacy, including what personal data is collected, why it is collected,
                            how it is used, how it is protected, and which individual rights users possess.
                        </p>
                        <p>
                            This privacy policy does not apply to, and excludes our responsibility, in terms of privacy,
                            information, or other practices of third parties, including Airbnb, Google, or other third
                            parties who manage websites or services to which the Navigate platform connects.
                        </p>
                        <p>
                            This privacy policy applies to all personal data that Navigate acquires or processes when
                            users interact with our platform. Examples include: creating photographer accounts, managing
                            photography assignments, connecting Google Calendar, uploading photos and videos,
                            coordinating with Airbnb hosts, and contacting customer service. These interactions may
                            involve photographers, hosts, administrators, and/or Airbnb partners.
                        </p>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-6">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>
                            II. General Information on Data Processing
                        </h2>
                        <p>
                            In this privacy policy, the term "personal data" signifies information that refers to users.
                            It allows us to identify users, directly or in combination with other information in our
                            possession.
                        </p>

                        <div className="flex flex-col gap-4">
                            <h3 className={PRIVACY_POLICY_SECTIONS_SUBTITLE_CLASSNAME}>
                                Users of the Platform in General
                            </h3>
                            <p>
                                Navigate processes user data based on consent and legitimate interest. By using or
                                consulting the Navigate platform, users explicitly agree to this privacy policy and
                                consent to the processing of their personal data in relation to the purposes and methods
                                described below, including any disclosure to third parties, if necessary, for the
                                provision of photography assignment services. The provision of data and, consequently,
                                the consent to the gathering and processing of data is optional. The user can deny
                                consent, and may revoke consent at any time. However, denying consent may make it
                                impossible to provide certain services and the platform experience may be compromised.
                            </p>
                        </div>

                        <div className="flex flex-col gap-4">
                            <h3 className={PRIVACY_POLICY_SECTIONS_SUBTITLE_CLASSNAME}>
                                Photographers and Service Providers
                            </h3>
                            <p>
                                Navigate shall do everything possible, consistent with the digital nature of our
                                service, to maintain the utmost confidentiality in its performance of photography
                                assignment coordination services. All assignment details, the nature of the photography
                                work performed, the personal data of photographers communicated for assignment purposes,
                                and any information transmitted by photographers to Navigate are subject to the
                                strictest confidentiality and the personal data collected shall be processed in
                                accordance with Article 6 of the GDPR.
                            </p>
                            <p>
                                In particular, Navigate shall use the information provided for the execution of
                                photography assignments (and for the eventual implementation of pre-contractual
                                measures) and such information can not — without the prior written consent of the
                                photographer — be disclosed publicly or to unauthorized third parties, except to Airbnb,
                                its authorized employees or their authorized subcontractors, should their operational or
                                advisory intervention require knowledge of such information for assignment coordination
                                and platform operation.
                            </p>
                        </div>

                        <div className="flex flex-col gap-4">
                            <h3 className={PRIVACY_POLICY_SECTIONS_SUBTITLE_CLASSNAME}>Google Calendar Integration</h3>
                            <div className={PRIVACY_POLICY_HIGHLIGHTED_CONTENT_CLASSNAME}>
                                <p>
                                    When users connect their Google Calendar to the Navigate platform, we collect and
                                    process the following Google user data:
                                </p>
                                <ul>
                                    <li>
                                        <strong>Google Profile Information:</strong> Email address, name, and basic
                                        profile information from your Google account
                                    </li>
                                    <li>
                                        <strong>Calendar Access:</strong> Read and write access to your Google Calendar
                                        events
                                    </li>
                                    <li>
                                        <strong>Calendar Events:</strong> Information about events we create, modify, or
                                        delete on your behalf for assignment scheduling
                                    </li>
                                </ul>
                                <p>
                                    <strong>Data Sharing for Google Calendar Integration:</strong>
                                </p>
                                <ul>
                                    <li>
                                        <strong>Airbnb:</strong> We share assignment scheduling information and calendar
                                        availability with Airbnb to coordinate photography services and ensure proper
                                        assignment management
                                    </li>
                                    <li>
                                        <strong>Internal Processing:</strong> Your Google calendar data is processed on
                                        our secure servers for the sole purpose of assignment scheduling and
                                        availability management
                                    </li>
                                    <li>
                                        <strong>Service Providers:</strong> Trusted third-party technical service
                                        providers may access Google data under strict data processing agreements solely
                                        for platform operation and maintenance
                                    </li>
                                </ul>
                                <p>
                                    <strong>We do NOT:</strong>
                                </p>
                                <ul>
                                    <li>Sell your Google data to any third parties</li>
                                    <li>Use your calendar data for advertising or marketing purposes</li>
                                    <li>Share personal calendar events unrelated to Airbnb assignments</li>
                                    <li>Transfer Google data to any unauthorized third parties</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-4">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>
                            III. Automatically Collected Information. Web Browsing Data
                        </h2>
                        <p>
                            As with all websites, the Navigate platform also makes use of log files in which information
                            from user visits is automatically collected and stored. The information collected could
                            include the following:
                        </p>
                        <ul>
                            <li>Internet Protocol (IP) Address</li>
                            <li>Type of browser and device parameters used to connect to the platform</li>
                            <li>Name of the Internet Service Provider (ISP)</li>
                            <li>Date and time of visit/request</li>
                            <li>Number of clicks, where applicable</li>
                            <li>Method used to submit the request to the server</li>
                            <li>Size of the file obtained in response</li>
                            <li>Digital code indicating the status of the response given by the server</li>
                            <li>Other parameters related to the operating system and the user's IT environment</li>
                        </ul>
                        <p>
                            The information listed above is handled automatically and collected in an exclusively
                            aggregated form in order to verify the correct functioning of the platform, and for security
                            reasons. For the purposes of security (spam filters, firewalls, virus detection),
                            automatically registered data may also include personal data such as IP address, which could
                            be used — in compliance with the laws in force on the subject — to block attempts to damage
                            the platform, harm other users, the carrying out of malicious activities, and activities
                            that constitute a crime.
                        </p>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-4">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>IV. Data Processing Location</h2>
                        <p>
                            The data collected by the Navigate platform is processed at the headquarters of the Data
                            Controller and at web hosting data centers. The web hosting services, which are responsible
                            for the processing of data and handle data on behalf of the Data Controller, are located in
                            the European Economic Area and act in accordance with European standards.
                        </p>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-4">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>
                            V. Transfer of Data in Non-EU Countries
                        </h2>
                        <p>
                            The Navigate platform may share some of the collected data with services located outside the
                            European Union area. In particular with Google (through Google Calendar API), and Airbnb
                            through photography assignment coordination services. The transfer is authorized on the
                            basis of specific rulings of the European Union and the Guarantor for the protection of
                            personal data, and appropriate safeguards are in place. These companies guarantee their
                            adherence to data protection standards equivalent to GDPR.
                        </p>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-4">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>VI. Safety Measures</h2>
                        <p>
                            The Navigate platform processes the data of users in a lawful and proper manner, adopting
                            appropriate security measures to prevent unauthorized access, disclosure, modification, or
                            unauthorized destruction of data. Processing is carried out using computer and/or electronic
                            tools, using appropriate measures at the organizational, technical, and administrative level
                            in order to protect personal data within our company, using criteria strictly related to the
                            photography assignment coordination purposes stated.
                        </p>
                        <p>
                            However, no data transmission and no storage system can be guaranteed as 100% secure. Users
                            who have reason to believe that their interactions with us are no longer secure are asked to
                            kindly report the issue immediately.
                        </p>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-4">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>VII. Data Retention</h2>
                        <p>
                            The personal data of users shall be kept for as long as necessary, or permitted, in light of
                            the purposes for which it is collected. The criteria adopted for determining storage times
                            are as follows:
                        </p>
                        <ul>
                            <li>The period of time in which users regularly use the Navigate platform</li>
                            <li>
                                The need to fulfill photography assignment contracts or legal obligations to which we
                                are subject
                            </li>
                            <li>
                                The opportunity to retain data in light of our legal position (for example, with regard
                                to time limits, disputes, or regulatory compliance checks)
                            </li>
                        </ul>
                        <div className={PRIVACY_POLICY_HIGHLIGHTED_CONTENT_CLASSNAME}>
                            <p>
                                <strong>Google Calendar Data Retention:</strong>
                            </p>
                            <ul>
                                <li>
                                    <strong>While Connected:</strong> Google Calendar data is retained as long as you
                                    maintain the Google Calendar connection
                                </li>
                                <li>
                                    <strong>After Disconnection:</strong> Google Calendar data is retained for up to 30
                                    days for operational purposes, then permanently deleted
                                </li>
                                <li>
                                    <strong>Assignment Records:</strong> Assignment-related calendar information may be
                                    retained for business record purposes as required by law
                                </li>
                            </ul>
                        </div>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-4">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>VIII. Rights of the User</h2>
                        <p>
                            Pursuant to the GDPR and Italian regulations, the user can, in the manner and within the
                            limits established by current legislation, exercise the following rights in relation to his
                            or her personal data:
                        </p>
                        <ul>
                            <li>
                                Request confirmation of the existence of personal data concerning him/her (right of
                                access)
                            </li>
                            <li>To learn its origin</li>
                            <li>Receive intelligible communications</li>
                            <li>
                                To have information about the criteria, the methods, and the purposes of the processing
                            </li>
                            <li>
                                Request the updating, rectification, integration, cancellation, transformation into
                                anonymous form, blocking of data processed in violation of the law
                            </li>
                            <li>
                                In cases of consent-based processing, receive data in a structured and readable format
                            </li>
                            <li>Right to file a complaint with the Supervisory Authority</li>
                            <li>Right to revoke Google Calendar access at any time through account settings</li>
                        </ul>
                        <p>
                            Requests should be addressed to the Data Controller. Users can exercise their rights by
                            accessing their account settings on the Navigate platform or by contacting the Data
                            Controller at the address provided below.
                        </p>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-4">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>IX. Third-Party Services</h2>
                        <p>
                            The Navigate platform integrates with Google Calendar through Google's APIs and is subject
                            to Google's Terms of Service and Privacy Policy. We comply with Google's API Services User
                            Data Policy, including the Limited Use requirements. The platform also integrates with
                            Airbnb's services for photography assignment coordination, which is subject to Airbnb's
                            Terms of Service and Privacy Policy.
                        </p>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-4 bg-neutral-100 border border-neutral-200 rounded-lg p-6">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>X. Data Controller</h2>
                        <p>
                            The Data Controller — in accordance with the GDPR and other Italian data protection laws, as
                            well as other provisions on data protection — is Translated S.r.l, Via Indonesia, No. 23,
                            00144, Rome, Italy, which can be contacted at the following email address:
                            info@translated.com.
                        </p>
                        <div className="flex flex-col gap-2">
                            <p>
                                <strong>Data Protection Officer:</strong> dpo@translated.com
                            </p>
                            <p>
                                <strong>General Contact:</strong> info@translated.com
                            </p>
                        </div>
                    </div>

                    <Separator orientation="horizontal" />

                    <div className="flex flex-col gap-4">
                        <h2 className={PRIVACY_POLICY_SECTIONS_TITLE_CLASSNAME}>XI. Updates</h2>
                        <p>
                            This privacy policy was last updated on {new Date().toLocaleDateString()}. We may update
                            this Privacy Policy periodically to reflect changes in our practices or for other
                            operational, legal, or regulatory reasons. We will notify you of material changes by email
                            or through our platform before they take effect.
                        </p>
                    </div>
                </div>
            </Container>

            <Footer />
        </div>
    )
}
